import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useInventory } from '../../../contexts/InventoryContext';
import InventoryFilters from '../search/InventoryFilters';
import InventorySearchTable from '../search/InventorySearchTable';

const defaultFilters = {
  project: '',
  status: '',
  category: '',
  priceMin: '',
  priceMax: '',
  facing: '',
};

function getCategoryKey(cat) {
  if (!cat) return '';
  return String(cat).toLowerCase().replace(/\s+/g, '_');
}

function filterUnits(units, searchQuery, filters) {
  let list = [...units];
  const q = (searchQuery || '').toLowerCase().trim();

  if (q) {
    list = list.filter(
      (u) =>
        (u.unitId && u.unitId.toLowerCase().includes(q)) ||
        (u.project && u.project.toLowerCase().includes(q)) ||
        (u.configuration && u.configuration.toLowerCase().includes(q)) ||
        (u.tower && u.tower.toLowerCase().includes(q)) ||
        (u.category && u.category.toLowerCase().includes(q))
    );
  }
  if (filters.project) {
    list = list.filter((u) => String(u.projectId) === String(filters.project));
  }
  if (filters.status) {
    list = list.filter((u) => String(u.status).toLowerCase() === String(filters.status).toLowerCase());
  }
  if (filters.category) {
    list = list.filter((u) => getCategoryKey(u.category) === getCategoryKey(filters.category));
  }
  if (filters.priceMin !== '' && filters.priceMin != null) {
    const min = Number(filters.priceMin);
    if (!Number.isNaN(min)) list = list.filter((u) => Number(u.price) >= min);
  }
  if (filters.priceMax !== '' && filters.priceMax != null) {
    const max = Number(filters.priceMax);
    if (!Number.isNaN(max)) list = list.filter((u) => Number(u.price) <= max);
  }
  if (filters.facing) {
    list = list.filter((u) => (u.facing || '').toLowerCase() === (filters.facing || '').toLowerCase());
  }
  return list;
}

/**
 * Search mode: unit-first results. Shown when URL has ?q=.
 * Data from context API (fetchSearchUnits returns mock; replace with real API in context).
 */
const SearchResult = ({ query }) => {
  const navigate = useNavigate();
  const { fetchSearchUnits } = useInventory();
  const [filters, setFilters] = useState(defaultFilters);
  const [units, setUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchSearchUnits().then((data) => {
      if (!cancelled) {
        setUnits(Array.isArray(data) ? data : []);
        setIsLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [fetchSearchUnits]);

  const filteredUnits = useMemo(
    () => filterUnits(units, query, filters),
    [units, query, filters]
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleProjectClick = (unit) => {
    if (unit.projectId) navigate(`/inventory/projects/${unit.projectId}`);
  };

  const handleConfigClick = (unit) => {
    if (unit.projectId && unit.configurationId) {
      navigate(`/inventory/projects/${unit.projectId}/configurations/${unit.configurationId}`);
    }
  };

  const handleBook = (unit) => {
    console.log('Book unit', unit);
  };

  const handleView = (unit) => {
    console.log('View unit', unit);
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100%', p: 3, pb: 4 }}>
      {/* Breadcrumb: Inventory > Search Results */}
      <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/inventory/projects')}
          sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Inventory
        </Link>
        <Typography variant="body2" color="text.primary" fontWeight={600}>
          Search Results
        </Typography>
      </Breadcrumbs>

      {/* Filters row */}
      <Box sx={{ mb: 2 }}>
        <InventoryFilters filters={filters} onFilterChange={handleFilterChange} />
      </Box>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {filteredUnits.length} Result{filteredUnits.length !== 1 ? 's' : ''} found
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} sx={{ color: 'success.main' }} />
        </Box>
      ) : filteredUnits.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            py: 8,
            px: 2,
            textAlign: 'center',
            borderRadius: 2,
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No results found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Try adjusting your search or filters
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Try searching: <strong>Lodha</strong>, <strong>Godrej</strong>, <strong>2BHK</strong>, <strong>3BHK</strong>, <strong>residential</strong>, <strong>commercial</strong>, or unit IDs like <strong>A-201</strong>, <strong>G-301</strong>
          </Typography>
        </Paper>
      ) : (
        <InventorySearchTable
          units={filteredUnits}
          onProjectClick={handleProjectClick}
          onConfigClick={handleConfigClick}
          onBook={handleBook}
          onView={handleView}
        />
      )}
    </Box>
  );
};

export default SearchResult;
